import { useEffect, useRef, useState, useCallback } from "react";
import { Link, Navigate } from "react-router-dom";
import { AlertTriangle, CameraOff, CheckCircle2, Clock, Loader2, Maximize2, Minimize2, QrCode, Scan, ScanLine, XCircle } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

const STATUS_MAP = {
  valid:        { color: "emerald", icon: CheckCircle2, title: "GELDIG",           sub: "Ticket toegang verleend" },
  already_used: { color: "orange",  icon: AlertTriangle, title: "AL GESCAND",       sub: "Dit ticket is eerder gebruikt" },
  wrong_event:  { color: "orange",  icon: AlertTriangle, title: "VERKEERD EVENT",   sub: "Ticket hoort bij een ander event" },
  wrong_org:    { color: "red",     icon: XCircle,       title: "GEEN TOEGANG",     sub: "Ticket hoort bij een andere organisatie" },
  not_found:    { color: "red",     icon: XCircle,       title: "ONBEKENDE CODE",   sub: "Deze ticketcode bestaat niet" },
  // Companion-app (avond4daagse) events only — see _a4d_scan_checkin on the backend.
  checked_in:   { color: "emerald", icon: CheckCircle2, title: "INGECHECKT",        sub: "Deelnemer is binnengekomen" },
  checked_out:  { color: "cyan",    icon: CheckCircle2, title: "UITGECHECKT",       sub: "Deelnemer is vertrokken" },
  // Multi-day registration ("Lichtjestour"-style events) only — see _mdr_scan_checkin.
  valid_override: { color: "orange", icon: AlertTriangle, title: "HANDMATIG INGECHECKT", sub: "Niet aangemeld voor deze dag — toch toegelaten" },
  wrong_day_for_scanner: { color: "red", icon: XCircle, title: "VERKEERDE DAG", sub: "Dit scanaccount mag niet scannen voor deze dag" },
};

// --- Scan feedback sound (Web Audio API -- no new asset) ---------------------
// A short, deliberately different tone alongside the existing vibration: a single
// rising "ding" for a successful scan, a lower double "buzz" for anything else
// (already scanned, wrong event, unknown code, etc). Best-effort only -- a browser
// that blocks audio without a prior user gesture (satisfied in practice by the
// operator's own tap on "Start camera" or the manual-code "Scan" button), or that
// has no Web Audio support at all, silently gets no sound; the existing vibration
// and on-screen result are unaffected either way.
let sharedScanAudioCtx = null;
function getScanAudioContext() {
  const AudioCtor = typeof window !== "undefined" && (window.AudioContext || window.webkitAudioContext);
  if (!AudioCtor) return null;
  if (!sharedScanAudioCtx) {
    try {
      sharedScanAudioCtx = new AudioCtor();
    } catch {
      return null;
    }
  }
  if (sharedScanAudioCtx.state === "suspended") {
    sharedScanAudioCtx.resume().catch(() => {});
  }
  return sharedScanAudioCtx;
}

function playScanTone(ctx, frequency, startDelay, duration) {
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = "sine";
  osc.frequency.value = frequency;
  const startAt = ctx.currentTime + startDelay;
  // Short exponential fade in/out avoids an audible click at the start/end of the tone.
  gain.gain.setValueAtTime(0.0001, startAt);
  gain.gain.exponentialRampToValueAtTime(0.2, startAt + 0.01);
  gain.gain.exponentialRampToValueAtTime(0.0001, startAt + duration);
  osc.connect(gain);
  gain.connect(ctx.destination);
  osc.start(startAt);
  osc.stop(startAt + duration + 0.02);
}

function playScanFeedbackSound(isSuccess) {
  try {
    const ctx = getScanAudioContext();
    if (!ctx) return;
    if (isSuccess) {
      playScanTone(ctx, 880, 0, 0.12);
    } else {
      // Two short, lower beeps -- audibly distinct from the single success "ding"
      // even for an operator not looking at the screen.
      playScanTone(ctx, 220, 0, 0.1);
      playScanTone(ctx, 220, 0.14, 0.1);
    }
  } catch { /* best-effort only -- never block the scan flow over an audio error */ }
}

// --- Scan session persistence (localStorage) ----------------------------------
// The "Recent" list is otherwise plain React state, lost on every page refresh --
// a real problem mid-event, since the scanner page can get reloaded (a dropped
// connection, a phone rotating, an accidental refresh) and the visible scan log
// would silently vanish even though every scan itself was already recorded
// server-side regardless. Scoped per event so switching between events (an
// org_admin/event_user scanning for more than one event) never mixes their lists.
const SCAN_HISTORY_STORAGE_PREFIX = "svj_admin_scan_history_v1";

function scanHistoryStorageKey(eventId) {
  return `${SCAN_HISTORY_STORAGE_PREFIX}:${eventId || "_"}`;
}

function loadScanHistory(eventId) {
  try {
    const raw = window.localStorage.getItem(scanHistoryStorageKey(eventId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveScanHistory(eventId, history) {
  try {
    window.localStorage.setItem(scanHistoryStorageKey(eventId), JSON.stringify(history));
  } catch { /* storage unavailable/full -- the in-memory session still works for this page view */ }
}

function ResultCard({ result }) {
  if (!result) return null;
  const meta = STATUS_MAP[result.status] || STATUS_MAP.not_found;
  const Icon = meta.icon;
  const styles = {
    emerald: "bg-emerald-500/15 border-emerald-500/50 text-emerald-200",
    orange:  "bg-svj-orange/15 border-svj-orange/50 text-svj-orange",
    red:     "bg-red-500/15 border-red-500/50 text-red-300",
    cyan:    "bg-svj-cyan/15 border-svj-cyan/50 text-svj-cyan",
  }[meta.color];
  return (
    <div data-testid={`scan-result-${result.status}`} className={`mt-4 rounded-2xl border-2 p-5 ${styles}`}>
      {result.ts && (
        <div className="text-[10px] uppercase tracking-widest font-semibold opacity-70 mb-2" data-testid="scan-result-scanned-at">
          Gescand om {new Date(result.ts).toLocaleTimeString("nl-NL")}
        </div>
      )}
      <div className="flex items-center gap-3">
        <Icon className="h-8 w-8" />
        <div>
          <div className="text-lg font-extrabold uppercase tracking-widest">{meta.title}</div>
          <div className="text-sm opacity-90">{meta.sub}</div>
        </div>
      </div>
      {result.ticket && (
        <div className="mt-4 pt-4 border-t border-current/20 text-sm">
          <div><span className="opacity-70">Type:</span> <b>{result.ticket.ticket_name}</b></div>
          {result.ticket.holder_name && <div><span className="opacity-70">Op naam:</span> {result.ticket.holder_name}</div>}
          <div><span className="opacity-70">Code:</span> <code className="text-xs">{result.ticket.code}</code></div>
          {result.ticket.used_at && (
            <div className="mt-1 inline-flex items-center gap-1 text-xs opacity-90">
              <Clock className="h-3.5 w-3.5" />
              {new Date(result.ticket.used_at).toLocaleString("nl-NL")}
              {result.ticket.scanner_label ? ` · door ${result.ticket.scanner_label}` : ""}
            </div>
          )}
        </div>
      )}
      {result.event?.title && (
        <div className="mt-2 text-xs opacity-70">
          Event: {result.event.title}
          {result.day ? ` · Dag ${result.day.day_number}${result.day.label ? ` (${result.day.label})` : ""}` : ""}
        </div>
      )}
    </div>
  );
}

// Companion-app only: shown instead of ResultCard when a scan can't tell which day to
// check the ticket in/out for (see the "need_day_selection" status from /admin/scan).
function DayPickerCard({ daySelection, onPick, onCancel }) {
  if (!daySelection) return null;
  return (
    <div className="mt-4 rounded-2xl border-2 border-svj-cyan/50 bg-svj-cyan/10 p-5" data-testid="scan-day-picker">
      <div className="text-sm font-extrabold uppercase tracking-widest text-svj-cyan">Kies de dag</div>
      <div className="mt-1 text-xs text-slate-300">Voor welke dag moet dit ticket in-/uitgecheckt worden?</div>
      <div className="mt-3 flex flex-wrap gap-2">
        {daySelection.days.map((d) => (
          <button
            key={d.id}
            type="button"
            onClick={() => onPick(d.id)}
            data-testid={`scan-day-pick-${d.id}`}
            className="rounded-full bg-gradient-to-r from-svj-orange via-svj-pink to-svj-purple text-white font-bold text-xs px-4 py-2"
          >
            Dag {d.day_number}{d.label ? ` — ${d.label}` : ""}
          </button>
        ))}
      </div>
      <button type="button" onClick={onCancel} className="mt-3 text-xs text-slate-400 hover:text-white">
        Annuleren
      </button>
    </div>
  );
}

// Multi-day registration ("Lichtjestour"-style events) only — shown instead of
// ResultCard when a scan comes in for a day the ticket isn't registered for (see
// the "not_registered_for_day" status from /admin/scan), structurally identical to
// DayPickerCard above: an inline, non-modal confirmation, never a dialog.
function OverrideCard({ notRegistered, onConfirm, onCancel }) {
  if (!notRegistered) return null;
  const { ticket, day } = notRegistered;
  return (
    <div className="mt-4 rounded-2xl border-2 border-svj-orange/50 bg-svj-orange/10 p-5" data-testid="scan-override-card">
      <div className="flex items-center gap-3">
        <AlertTriangle className="h-8 w-8 text-svj-orange" />
        <div>
          <div className="text-lg font-extrabold uppercase tracking-widest text-svj-orange">NIET AANGEMELD</div>
          <div className="text-sm opacity-90 text-svj-orange">
            {ticket?.holder_name || ticket?.ticket_name} is niet aangemeld voor Dag {day?.day_number}{day?.label ? ` (${day.label})` : ""}
          </div>
        </div>
      </div>
      <button
        type="button"
        onClick={onConfirm}
        data-testid="scan-override-confirm"
        className="mt-4 w-full rounded-full bg-gradient-to-r from-svj-orange via-svj-pink to-svj-purple text-white font-bold text-sm px-4 py-2.5"
      >
        Toch inchecken
      </button>
      <button type="button" onClick={onCancel} data-testid="scan-override-cancel" className="mt-2 text-xs text-slate-400 hover:text-white">
        Annuleren
      </button>
    </div>
  );
}

export default function AdminScan() {
  const { authAxios, me } = useAuth();
  const [events, setEvents] = useState([]);
  const [eventId, setEventId] = useState("");
  const [scannerActive, setScannerActive] = useState(false);
  const [manualCode, setManualCode] = useState("");
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [cameraError, setCameraError] = useState(null);
  const [daySelection, setDaySelection] = useState(null); // { code, days } — companion-app only
  const [notRegistered, setNotRegistered] = useState(null); // { code, eventDayId, ticket, day } — multi-day registration only
  const scannerRef = useRef(null);
  const startingRef = useRef(false);
  const cameraCardRef = useRef(null);
  const lastCodeRef = useRef({ code: "", ts: 0 });
  const mountedRef = useRef(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [liveStats, setLiveStats] = useState({ scanned_today: 0, scanned_total: 0, sold: 0 });
  const [presenceEventId, setPresenceEventId] = useState("");
  const [presenceStats, setPresenceStats] = useState(null);
  const [flashValid, setFlashValid] = useState(false);
  const flashTimeoutRef = useRef(null);

  const refreshStats = useCallback(async () => {
    try {
      const { data } = await authAxios.get("/admin/scan/stats", {
        params: eventId ? { event_id: eventId } : {},
      });
      if (mountedRef.current) setLiveStats(data);
    } catch { /* silent — stats are a nice-to-have on this screen */ }
  }, [authAxios, eventId]);

  const refreshPresence = useCallback(async (targetEventId = presenceEventId) => {
    if (!targetEventId) return;
    try {
      const { data } = await authAxios.get("/admin/scan/presence", {
        params: { event_id: targetEventId },
      });
      if (mountedRef.current) setPresenceStats(data);
    } catch { /* silent — the compact attendance block is supplemental */ }
  }, [authAxios, presenceEventId]);

  useEffect(() => {
    const onFsChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onFsChange);
    return () => document.removeEventListener("fullscreenchange", onFsChange);
  }, []);

  const toggleFullscreen = async () => {
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
        return;
      }
      const el = cameraCardRef.current;
      if (!el) return;
      // Enter fullscreen FIRST, then start the camera — html5-qrcode measures the
      // qr-container on start(), so it must already be at its fullscreen size by then.
      // If a session was already running, the isFullscreen effect below takes care of
      // stopping + restarting it at the new size instead.
      if (el.requestFullscreen) await el.requestFullscreen();
      else if (el.webkitRequestFullscreen) await el.webkitRequestFullscreen();
      else throw new Error("Fullscreen niet ondersteund door deze browser");
      if (!scannerActive && !startingRef.current) await startScanner();
    } catch (e) {
      toast.error(e?.message || "Kon niet naar fullscreen");
    }
  };

  const stopScanner = useCallback(async () => {
    // Snapshot + clear the ref first so a re-entrant call (unmount during stop) becomes a no-op.
    const s = scannerRef.current;
    scannerRef.current = null;
    if (mountedRef.current) setScannerActive(false);
    if (!s) return;
    try {
      // html5-qrcode's isScanning flag prevents "not currently running" errors on double-stop.
      if (typeof s.isScanning === "boolean" ? s.isScanning : true) {
        await s.stop();
      }
    } catch { /* library throws on already-stopped — safe to ignore */ }
    try { await s.clear(); } catch { /* same */ }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    (async () => {
      try {
        const { data } = await authAxios.get("/admin/scan/events");
        if (!mountedRef.current) return;
        setEvents(data.items || []);
        if (data.items?.length === 1) setEventId(data.items[0].id);
      } catch (e) {
        toast.error(e?.response?.data?.detail || e.message);
      }
    })();
    return () => {
      mountedRef.current = false;
      // Fire and forget — leaf-only qr-container means React won't crash during teardown.
      stopScanner();
      if (flashTimeoutRef.current) clearTimeout(flashTimeoutRef.current);
    };
  }, [authAxios, stopScanner]);

  // Refresh live scan counter whenever the selected event changes, and every 30s while open.
  useEffect(() => {
    refreshStats();
    const t = setInterval(refreshStats, 30000);
    return () => clearInterval(t);
  }, [refreshStats]);

  useEffect(() => {
    const selectedEvent = events.find((event) => event.id === eventId);
    if (selectedEvent?.companion_app_enabled) {
      setPresenceEventId(selectedEvent.id);
    } else if (eventId) {
      setPresenceEventId("");
      setPresenceStats(null);
    }
  }, [eventId, events]);

  useEffect(() => {
    if (!presenceEventId) return undefined;
    refreshPresence();
    const timer = setInterval(refreshPresence, 30000);
    return () => clearInterval(timer);
  }, [presenceEventId, refreshPresence]);

  // Real reported bug: the "Recent" list was plain React state only, so refreshing
  // (or just reopening) this page silently lost the whole visible scan log for the
  // session, even though every scan was already safely recorded server-side. Restore
  // it from localStorage whenever the selected event (re)resolves, and persist it back
  // on every change — scoped per event so switching events never mixes their lists.
  useEffect(() => {
    setHistory(loadScanHistory(eventId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  useEffect(() => {
    saveScanHistory(eventId, history);
  }, [eventId, history]);

  const resetScanSession = () => {
    if (history.length === 0) return;
    if (!window.confirm("Scansessie wissen? Dit verwijdert alleen de lijst op dit scherm — gescande tickets blijven gewoon geregistreerd.")) return;
    setHistory([]);
    setResult(null);
    setDaySelection(null);
    setNotRegistered(null);
  };

  const doScan = async (code, eventDayId, override) => {
    const now = Date.now();
    // Collapses duplicate decodes of the SAME held-in-view code (html5-qrcode fires this
    // callback on every scanned frame at ~10fps for as long as the code stays in the
    // camera's view). Real reported bug: at the old 600ms window, simply holding a ticket
    // steady in front of the camera for a bit longer than that — completely normal while
    // the operator reads the on-screen result — fired a SECOND scan of the exact same
    // code, which then correctly (but confusingly) came back "already scanned" moments
    // after the first scan had just succeeded. 4s comfortably covers that normal pause
    // without meaningfully slowing down a deliberate rescan of the same code (move the
    // ticket away and back, or just wait a few seconds). Skipped when resubmitting after
    // a day pick (eventDayId set) or an explicit override confirmation — those are
    // deliberate, explicit actions, not a duplicate frame.
    if (!eventDayId && !override && lastCodeRef.current.code === code && now - lastCodeRef.current.ts < 4000) return;
    lastCodeRef.current = { code, ts: now };
    setScanning(true);
    try {
      const { data } = await authAxios.post("/admin/scan", {
        code,
        event_id: eventId || undefined,
        event_day_id: eventDayId || undefined,
        override: !!override,
      });
      if (!mountedRef.current) return;
      // Companion-app events with more than one day and no date match for today: ask the
      // scanner which day this check-in is for instead of guessing (see
      // _a4d_resolve_event_day on the backend). Doesn't touch result/history/flash/vibrate.
      if (data.status === "need_day_selection") {
        setDaySelection({ code, days: data.days || [] });
        return;
      }
      setDaySelection(null);
      // Multi-day registration ("Lichtjestour"-style events) only: the ticket isn't
      // registered for this day — show the inline override card instead of a result,
      // same "ask, don't guess" mechanic as need_day_selection above.
      if (data.status === "not_registered_for_day") {
        setNotRegistered({ code, eventDayId, ticket: data.ticket, day: data.day });
        return;
      }
      setNotRegistered(null);
      // ts marks THIS scan attempt (shown as "Gescand om …" in ResultCard) — distinct from
      // result.ticket.used_at, which is when the ticket was originally used and rightly
      // doesn't change on repeat scans of an already-used code.
      setResult({ ...data, ts: now });
      setHistory((h) => [{ ...data, ts: now }, ...h].slice(0, 25));
      const isSuccess = data.status === "valid" || data.status === "checked_in" || data.status === "checked_out" || data.status === "valid_override";
      if (isSuccess) {
        refreshStats();
        if (data.event?.companion_app_enabled && data.event?.id) {
          setPresenceEventId(data.event.id);
          refreshPresence(data.event.id);
        } else if (!eventId) {
          setPresenceEventId("");
          setPresenceStats(null);
        }
        // Briefly flashes the scan-frame square green — cleared on unmount via flashTimeoutRef.
        setFlashValid(true);
        if (flashTimeoutRef.current) clearTimeout(flashTimeoutRef.current);
        flashTimeoutRef.current = setTimeout(() => {
          if (mountedRef.current) setFlashValid(false);
        }, 800);
      }
      if (typeof navigator !== "undefined" && navigator.vibrate) {
        navigator.vibrate(isSuccess ? 80 : [40, 40, 40]);
      }
      playScanFeedbackSound(isSuccess);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      if (mountedRef.current) setScanning(false);
    }
  };

  const startScanner = async () => {
    if (startingRef.current || scannerRef.current) return;
    startingRef.current = true;
    setCameraError(null);
    try {
      const { Html5Qrcode } = await import("html5-qrcode");
      // CRUCIAL: flip to "active" BEFORE calling start() so React commits the DOM change
      // and the qr-container has real dimensions when html5-qrcode measures it. Otherwise
      // start() reads 0-width and the camera never comes up (or comes up sideways).
      setScannerActive(true);
      await new Promise((r) => requestAnimationFrame(() => r()));
      // Second frame — belt-and-braces so layout is fully settled on slower devices.
      await new Promise((r) => requestAnimationFrame(() => r()));
      const instance = new Html5Qrcode("svj-qr-container", { verbose: false });
      await instance.start(
        { facingMode: "environment" },
        {
          fps: 10,
          // Callback keeps the scan box perfectly square and proportional to the viewport,
          // whether we're in a small card or fullscreen on a portrait phone.
          qrbox: (vw, vh) => {
            const side = Math.floor(Math.min(vw, vh) * 0.7);
            return { width: side, height: side };
          },
          aspectRatio: 1.0,
        },
        (decoded) => { doScan(decoded); },
        () => { /* ignore frame errors */ },
      );
      if (!mountedRef.current) {
        try { await instance.stop(); } catch { /* ignore */ }
        try { await instance.clear(); } catch { /* ignore */ }
        return;
      }
      scannerRef.current = instance;
    } catch (e) {
      setCameraError(e?.message || "Kon camera niet starten");
      setScannerActive(false);
    } finally {
      startingRef.current = false;
    }
  };

  // Restart an already-running scanner whenever isFullscreen actually flips (in either
  // direction). html5-qrcode measures #svj-qr-container once at start() and never
  // re-measures it, so if fullscreen is entered/exited while scanning, the scan box
  // stays stretched/off-center and no longer lines up with what the camera shows —
  // stopping + restarting forces a fresh measurement at the new container size. Skip
  // the very first render (isFullscreen starts false) so mount doesn't trigger this.
  const isFirstFsRenderRef = useRef(true);
  useEffect(() => {
    if (isFirstFsRenderRef.current) {
      isFirstFsRenderRef.current = false;
      return;
    }
    if (!scannerRef.current) return;
    (async () => {
      await stopScanner();
      if (!mountedRef.current) return;
      // Same double-rAF settle as startScanner uses, so the container has fully
      // committed its new (fullscreen or windowed) size before we measure again.
      await new Promise((r) => requestAnimationFrame(() => r()));
      await new Promise((r) => requestAnimationFrame(() => r()));
      if (!mountedRef.current) return;
      await startScanner();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFullscreen]);

  // Scanner-only accounts have no business seeing anything else; belt-and-braces guard
  // in case someone lands here without a role.
  if (me && !["superadmin", "org_owner", "org_admin", "event_user", "scanner"].includes(me.role)) {
    return <Navigate to="/admin" replace />;
  }

  return (
    <div data-testid="admin-scan-page">
      <div className="text-xs uppercase tracking-widest text-svj-cyan font-semibold">Bij de deur</div>
      <h1 className="mt-2 text-3xl sm:text-4xl font-extrabold text-white">Tickets scannen</h1>
      <p className="mt-1 text-sm text-slate-400">
        Kies een event, richt de camera op de QR-code, of typ de code handmatig.
      </p>

      <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="scan-live-widget">
        <div className="rounded-2xl border border-svj-cyan/50 bg-gradient-to-br from-svj-cyan/10 to-transparent p-4">
          <div className="text-[10px] uppercase tracking-widest text-svj-cyan font-semibold">Vandaag gescand</div>
          <div className="mt-1 text-3xl font-extrabold text-white" data-testid="scan-live-today">
            {liveStats.scanned_today}
          </div>
          <div className="text-[11px] text-slate-400">
            {eventId ? "voor dit event" : "over alle events"}
          </div>
        </div>
        <div className="rounded-2xl border border-svj-border bg-svj-card p-4">
          <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Totaal gescand</div>
          <div className="mt-1 text-3xl font-extrabold text-white" data-testid="scan-live-total">
            {liveStats.scanned_total}
            <span className="text-lg text-slate-400 font-semibold"> / {liveStats.sold}</span>
          </div>
          <div className="text-[11px] text-slate-400">
            {liveStats.sold ? `${Math.round((liveStats.scanned_total / liveStats.sold) * 100)}% van verkochte tickets` : "geen verkopen"}
          </div>
        </div>
        <div className="rounded-2xl border border-svj-border bg-svj-card p-4">
          <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Nog te scannen</div>
          <div className="mt-1 text-3xl font-extrabold text-white" data-testid="scan-live-remaining">
            {Math.max(0, (liveStats.sold || 0) - (liveStats.scanned_total || 0))}
          </div>
          <div className="text-[11px] text-slate-400">
            wordt live bijgewerkt
          </div>
        </div>
      </div>

      {presenceEventId && presenceStats && (
        <div className="mt-4 rounded-2xl border border-svj-cyan/40 bg-svj-card p-4" data-testid="scan-presence-widget">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-svj-cyan font-semibold">Presentie vandaag</div>
              <div className="mt-1 text-sm font-bold text-white">
                {presenceStats.event?.title}
                {presenceStats.day ? ` · Dag ${presenceStats.day.day_number}${presenceStats.day.label ? ` (${presenceStats.day.label})` : ""}` : ""}
              </div>
            </div>
            {me?.role !== "scanner" && (
              <Link to="/admin/presence" className="text-xs font-semibold text-svj-cyan hover:text-white">
                Volledige presentielijst
              </Link>
            )}
          </div>

          {presenceStats.day ? (
            <div className="mt-3 grid grid-cols-3 gap-3">
              <div className="rounded-xl bg-svj-bg/60 border border-svj-border p-3 text-center">
                <div className="text-2xl font-extrabold text-svj-cyan" data-testid="scan-presence-in">{presenceStats.counts.in}</div>
                <div className="text-[10px] uppercase tracking-widest text-slate-400">Binnen</div>
              </div>
              <div className="rounded-xl bg-svj-bg/60 border border-svj-border p-3 text-center">
                <div className="text-2xl font-extrabold text-emerald-400" data-testid="scan-presence-done">{presenceStats.counts.done}</div>
                <div className="text-[10px] uppercase tracking-widest text-slate-400">Klaar</div>
              </div>
              <div className="rounded-xl bg-svj-bg/60 border border-svj-border p-3 text-center">
                <div className="text-2xl font-extrabold text-slate-300" data-testid="scan-presence-not-seen">{presenceStats.counts.not_seen}</div>
                <div className="text-[10px] uppercase tracking-widest text-slate-400">Niet gezien</div>
              </div>
            </div>
          ) : (
            <div className="mt-3 text-xs text-slate-400">Voor vandaag is geen eventdag ingesteld.</div>
          )}
        </div>
      )}

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          <label className="block">
            <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">Event (optioneel — laat leeg om alles te scannen)</span>
            <select
              value={eventId}
              onChange={(e) => setEventId(e.target.value)}
              data-testid="scan-event-select"
              className="mt-1 w-full h-11 px-3 rounded-lg bg-svj-card border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan"
            >
              <option value="">Alle events die ik mag scannen</option>
              {events.map((e) => (
                <option key={e.id} value={e.id}>{e.title} · {new Date(e.starts_at).toLocaleDateString("nl-NL")}</option>
              ))}
            </select>
          </label>

          <div
            ref={cameraCardRef}
            data-testid="scan-camera-card"
            className={`rounded-2xl border border-svj-border bg-svj-card p-4 ${
              isFullscreen ? "fixed inset-0 z-[100] rounded-none border-0 flex flex-col bg-black p-3" : ""
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm font-semibold text-white uppercase tracking-wider">
                <QrCode className="h-4 w-4 text-svj-cyan" /> Camera scanner
              </div>
              <div className="flex items-center gap-2">
                {scannerActive ? (
                  <button type="button" onClick={stopScanner} data-testid="scan-stop" className="rounded-full border border-svj-border text-slate-300 hover:text-white text-xs font-semibold px-3 py-1.5 inline-flex items-center gap-1">
                    <CameraOff className="h-3.5 w-3.5" /> Stop
                  </button>
                ) : (
                  <button type="button" onClick={startScanner} data-testid="scan-start" className="rounded-full bg-gradient-to-r from-svj-orange via-svj-pink to-svj-purple text-white font-bold text-xs px-4 py-1.5 inline-flex items-center gap-1">
                    <Scan className="h-3.5 w-3.5" /> Start camera
                  </button>
                )}
                <button
                  type="button"
                  onClick={toggleFullscreen}
                  data-testid="scan-fullscreen"
                  title={isFullscreen ? "Verlaat fullscreen" : "Fullscreen"}
                  className="rounded-full border border-svj-cyan/60 text-svj-cyan hover:bg-svj-cyan/10 text-xs font-semibold px-3 py-1.5 inline-flex items-center gap-1"
                >
                  {isFullscreen ? <Minimize2 className="h-3.5 w-3.5" /> : <Maximize2 className="h-3.5 w-3.5" />}
                  {isFullscreen ? "Verlaat" : "Fullscreen"}
                </button>
              </div>
            </div>

            {/* Relative positioning context for the fullscreen result overlay below — a
                SIBLING of the qr-container, never a child of it (see the guard on that div). */}
            <div className={isFullscreen ? "relative flex-1 mt-0 min-h-0" : "relative mt-3"}>
              {/* CRITICAL: qr-container is a bare leaf div that React must NEVER put children in.
                  html5-qrcode injects/removes its own DOM here; mixing React children triggers
                  "Failed to execute removeChild on Node" during unmount. Placeholder lives outside.
                  In fullscreen we blow it up to the full viewport so the QR fills the phone screen. */}
              <div
                id="svj-qr-container"
                data-testid="scan-camera"
                style={{
                  width: "100%",
                  // A definite `height` (not just min-height) so percentage-height children
                  // (the video, see index.css) resolve correctly instead of collapsing to 0.
                  // In fullscreen this is 100% of the flex-1 wrapper's actual allocated space
                  // (not 100dvh, which could render taller than that space and throw off the
                  // bottom-anchored result overlay's alignment with the visible video).
                  height: scannerActive ? (isFullscreen ? "100%" : 320) : 0,
                  maxWidth: isFullscreen ? "100%" : undefined,
                }}
                className={`rounded-xl overflow-hidden ${isFullscreen ? "bg-black" : "bg-svj-bg"} ${scannerActive ? "" : "h-0"}`}
              />

              {/* Our own scan-frame square — html5-qrcode's built-in one (#qr-shaded-region)
                  is hidden in index.css. Sibling of qr-container, not nested in it (same
                  "no React children in that leaf div" rule as always). width 70% + aspect-
                  square + max-height 70% gives min(70% of width, 70% of height), matching the
                  qrbox callback's own Math.min(vw, vh) * 0.7 without needing to measure the
                  container in JS. Flashes green briefly on a valid scan (flashValid, set in
                  doScan). pointer-events-none so it never blocks camera/tap interaction. */}
              {scannerActive && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div
                    data-testid="scan-frame"
                    style={{ width: "70%", maxHeight: "70%" }}
                    className={`aspect-square rounded-2xl border-[3px] transition-colors duration-300 ${
                      flashValid
                        ? "border-emerald-400 bg-emerald-400/10 shadow-[0_0_20px_rgba(16,185,129,0.55)]"
                        : "border-white/70"
                    }`}
                  />
                </div>
              )}

              {/* Fullscreen result: floats bottom-anchored OVER the camera feed instead of
                  stacking below it, so you never scroll/look away from the video to see it.
                  Bottom-anchored + capped height keeps the centered qrbox clear for rescans.
                  z-10 keeps it above the video but it never reaches the header buttons above —
                  those live outside this relative wrapper entirely. */}
              {isFullscreen && (result || daySelection || notRegistered) && (
                <div className="absolute inset-x-0 bottom-0 z-10 max-h-[45%] overflow-y-auto px-3 pb-3 pt-10 pointer-events-none bg-gradient-to-t from-black/90 via-black/60 to-transparent">
                  <div className="pointer-events-auto">
                    {daySelection ? (
                      <DayPickerCard daySelection={daySelection} onPick={(dayId) => doScan(daySelection.code, dayId)} onCancel={() => setDaySelection(null)} />
                    ) : notRegistered ? (
                      <OverrideCard notRegistered={notRegistered} onConfirm={() => doScan(notRegistered.code, notRegistered.eventDayId, true)} onCancel={() => setNotRegistered(null)} />
                    ) : (
                      <ResultCard result={result} />
                    )}
                  </div>
                </div>
              )}
            </div>
            {!scannerActive && !isFullscreen && (
              <div className="mt-3 rounded-xl overflow-hidden bg-svj-bg aspect-video flex items-center justify-center">
                <div className="text-slate-500 text-sm text-center px-6">
                  <ScanLine className="h-8 w-8 mx-auto mb-2 text-svj-cyan" />
                  Klik &quot;Start camera&quot; om QR-codes te scannen.<br/>
                  Werkt het beste op een telefoon met HTTPS.
                </div>
              </div>
            )}

            {cameraError && (
              <div className="mt-3 text-xs text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg p-3">
                {cameraError}
              </div>
            )}

            {/* Normal (non-fullscreen) view only — appears right where the eye already is
                after scanning, instead of below the separate manual-code form. In
                fullscreen the overlay above takes over so this stays hidden there. */}
            {!isFullscreen && (
              daySelection ? (
                <DayPickerCard daySelection={daySelection} onPick={(dayId) => doScan(daySelection.code, dayId)} onCancel={() => setDaySelection(null)} />
              ) : notRegistered ? (
                <OverrideCard notRegistered={notRegistered} onConfirm={() => doScan(notRegistered.code, notRegistered.eventDayId, true)} onCancel={() => setNotRegistered(null)} />
              ) : (
                <ResultCard result={result} />
              )
            )}
          </div>

          <form onSubmit={(e) => { e.preventDefault(); if (manualCode.trim()) doScan(manualCode.trim()); }} className="rounded-2xl border border-svj-border bg-svj-card p-4">
            <div className="flex items-center gap-2 text-sm font-semibold text-white uppercase tracking-wider">
              <ScanLine className="h-4 w-4 text-svj-cyan" /> Handmatige code
            </div>
            <div className="mt-3 flex gap-2">
              <input
                value={manualCode}
                onChange={(e) => setManualCode(e.target.value)}
                data-testid="scan-manual-input"
                placeholder="SVJ-XXXXXXXXXXXX"
                className="flex-1 h-11 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-base font-mono focus:outline-none focus:ring-2 focus:ring-svj-cyan"
              />
              <button type="submit" disabled={scanning} data-testid="scan-manual-submit" className="rounded-full bg-gradient-to-r from-svj-orange via-svj-pink to-svj-purple text-white font-bold text-sm px-5 inline-flex items-center gap-2">
                {scanning ? <Loader2 className="h-4 w-4 animate-spin" /> : <Scan className="h-4 w-4" />}
                Scan
              </button>
            </div>
          </form>
        </div>

        {/* History */}
        <aside className="lg:col-span-1">
          <div className="rounded-2xl border border-svj-border bg-svj-card p-4 lg:sticky lg:top-6">
            <div className="flex items-center justify-between mb-3">
              <div className="text-sm font-semibold text-white uppercase tracking-wider">Recent</div>
              {history.length > 0 && (
                <button
                  type="button"
                  onClick={resetScanSession}
                  data-testid="scan-history-reset"
                  className="text-[11px] text-slate-400 hover:text-white"
                >
                  Wis sessie
                </button>
              )}
            </div>
            {history.length === 0 && <div className="text-xs text-slate-500">Nog geen scans in deze sessie.</div>}
            <ul className="space-y-2 max-h-[70vh] overflow-y-auto pr-1">
              {history.map((h, i) => {
                const meta = STATUS_MAP[h.status] || STATUS_MAP.not_found;
                const colors = { emerald: "text-emerald-400", orange: "text-svj-orange", red: "text-red-400" }[meta.color];
                return (
                  <li key={i} className="text-xs border-b border-svj-border pb-2 last:border-b-0">
                    <div className={`font-bold uppercase tracking-widest ${colors}`}>{meta.title}</div>
                    <div className="text-slate-300 truncate">{h.ticket?.code || "—"}</div>
                    {h.ticket?.holder_name && <div className="text-slate-500">{h.ticket.holder_name}</div>}
                    <div className="text-slate-600">{new Date(h.ts).toLocaleTimeString("nl-NL")}</div>
                  </li>
                );
              })}
            </ul>
          </div>
        </aside>
      </div>
    </div>
  );
}
